/**
 * Property Repository
 *
 * Lazy, per-property read endpoints (counts, totals, luna images, FDNY) that
 * hydrate the overview page after first paint. The primary address-based
 * overview fetch stays in reducers/propertyReducer.js.
 *
 * Today these thunks fetch and the results are bridged onto `propertyData`
 * (single read source, keeps existing consumers + genericDataSetReducer count
 * coupling working). The repo slice also holds the data so consumers can be
 * migrated to `useProperty()` incrementally in the future.
 */

import { createRepository } from 'data/utils/createRepository';
import { getApiUrl } from 'data/services/apiConstants';
import { get } from 'data/services/apiService';

const propertyRepository = createRepository({
	endpoint: 'property',
	name: 'propertyRepo',
	statePath: 'propertyRepo',

	customMethods: {
		/**
		 * Per-dataset and combined open counts.
		 * Usage: actions.getCounts({ propertyId })
		 */
		getCounts: ({ propertyId, authToken, dispatch }) =>
			get({
				url: `${getApiUrl()}/api/property/${propertyId}/counts`,
				authToken,
				dispatch,
			}),

		/**
		 * Penalties/refunds totals.
		 * Usage: actions.getTotals({ propertyId })
		 */
		getTotals: ({ propertyId, authToken, dispatch }) =>
			get({
				url: `${getApiUrl()}/api/property/${propertyId}/totals`,
				authToken,
				dispatch,
			}),

		/**
		 * Luna historical images.
		 * Usage: actions.getLunaImages({ propertyId })
		 */
		getLunaImages: ({ propertyId, authToken, dispatch }) =>
			get({
				url: `${getApiUrl()}/api/property/${propertyId}/luna-images`,
				authToken,
				dispatch,
			}),

		/**
		 * FDNY overview (dispatches a background refresh server-side when stale).
		 * Usage: actions.getFdnyOverview({ propertyId })
		 */
		getFdnyOverview: ({ propertyId, authToken, dispatch }) =>
			get({
				url: `${getApiUrl()}/api/property/${propertyId}/fdny-overview`,
				authToken,
				dispatch,
			}),
	},
});

export const {
	statePath,
	thunks: propertyRepoThunks,
	reducer: propertyRepoReducer,
	actions: propertyRepoActions,
	types: propertyRepoTypes,
	selectors: propertyRepoSelectors,
	useHook: useProperty,
} = propertyRepository;

export { propertyRepository as propertyRepo };
export default propertyRepository;
